import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { Stack, Typography } from '@sureco/design-system'
import {
  Button,
  Card,
  Input,
  Modal,
  Table,
  Tooltip,
} from '@surecompanies/core_components'
import type { ColumnsType } from 'antd/es/table'
import { CopyableValue } from 'components/CopyableValue'
import RHFInput from 'enrollment-platform/components/form/hookform/Input/RHFInput'
import RHFSelect from 'enrollment-platform/components/form/hookform/Select/RHFSelect'
import React, { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import type { AgentStateLicense } from 'enrollment-platform/api/partner-benefit-graph/ideon-agents.types'
import { formatStateLabel, stateOptions } from './shared'
import { useTablePagination } from './useTablePagination'

const srOnlyStyle: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0,
}

type LicenseFormValues = {
  state: string
  licenseNumber: string
}

const emptyLicense: LicenseFormValues = { state: '', licenseNumber: '' }

export type StateLicensesCardProps = {
  loading: boolean
  stateLicenses: AgentStateLicense[]
  addStateLicense: (body: {
    state: string
    licenseNumber: string
  }) => Promise<boolean>
  editStateLicense: (
    id: string,
    body: { state: string; licenseNumber: string }
  ) => Promise<boolean>
  removeStateLicense: (id: string) => Promise<boolean>
}

export default function StateLicensesCard({
  loading,
  stateLicenses,
  addStateLicense,
  editStateLicense,
  removeStateLicense,
}: StateLicensesCardProps) {
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<AgentStateLicense | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [search, setSearch] = useState('')

  const filteredLicenses = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return stateLicenses
    return stateLicenses.filter((l) =>
      [l.id, l.state, formatStateLabel(l.state), l.licenseNumber]
        .join(' ')
        .toLowerCase()
        .includes(q)
    )
  }, [stateLicenses, search])

  const { paginationProps, resetToFirstPage } = useTablePagination(
    filteredLicenses.length
  )

  const onSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value)
      // Reset to page 1 so the user doesn't land on an empty page when the
      // filtered set shrinks.
      resetToFirstPage()
    },
    [resetToFirstPage]
  )

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = useForm<LicenseFormValues>({
    mode: 'onChange',
    defaultValues: emptyLicense,
  })

  const stateOptionsForSelect = useMemo(() => {
    const taken = new Set<string>()
    for (const l of stateLicenses) {
      if (l.id !== editing?.id) taken.add(l.state)
    }
    return stateOptions.map((o) => ({ ...o, disabled: taken.has(o.value) }))
  }, [stateLicenses, editing])

  const canAdd = useMemo(() => {
    const taken = new Set(stateLicenses.map((l) => l.state))
    return taken.size < stateOptions.length
  }, [stateLicenses])

  const openAdd = useCallback(() => {
    setEditing(null)
    reset(emptyLicense)
    setModalOpen(true)
  }, [reset])

  const openEdit = useCallback(
    (row: AgentStateLicense) => {
      setEditing(row)
      reset({
        state: row.state,
        licenseNumber: row.licenseNumber,
      })
      setModalOpen(true)
    },
    [reset]
  )

  const submitModal = handleSubmit(async (v) => {
    if (submitting) return
    setSubmitting(true)
    try {
      const ok = editing?.id
        ? await editStateLicense(editing.id, {
            state: v.state,
            licenseNumber: v.licenseNumber,
          })
        : await addStateLicense({
            state: v.state,
            licenseNumber: v.licenseNumber,
          })
      if (ok) {
        setModalOpen(false)
        reset(emptyLicense)
      }
    } finally {
      setSubmitting(false)
    }
  })

  const confirmDelete = useCallback(
    (row: AgentStateLicense) => {
      const stateLabel = formatStateLabel(row.state)
      const subject =
        stateLabel !== '—'
          ? `the state license for ${stateLabel}`
          : 'this state license'
      Modal.confirm({
        title: 'Delete state license?',
        content: `Remove ${subject}?`,
        okText: 'Delete',
        okType: 'danger',
        // Reject so Ant keeps the dialog open when the request fails — the
        // error toast appears, the user can retry or cancel.
        onOk: async () => {
          const ok = await removeStateLicense(row.id)
          if (!ok) return Promise.reject()
        },
      })
    },
    [removeStateLicense]
  )

  const columns = useMemo<ColumnsType<AgentStateLicense>>(
    () => [
      {
        title: 'State',
        dataIndex: 'state',
        key: 'state',
        render: (code?: string) => formatStateLabel(code),
      },
      {
        title: 'License Number',
        dataIndex: 'licenseNumber',
        key: 'licenseNumber',
        render: (licenseNumber?: string) => (
          <CopyableValue value={licenseNumber} />
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 100,
        render: (_: unknown, row: AgentStateLicense) => (
          <Stack direction="row" space={8}>
            <Tooltip title="Edit">
              <Button
                type="default"
                icon={<EditOutlined aria-hidden="true" />}
                onClick={() => openEdit(row)}
                aria-label="Edit state license"
              />
            </Tooltip>
            <Tooltip title="Delete">
              <Button
                type="default"
                danger
                icon={<DeleteOutlined aria-hidden="true" />}
                onClick={() => confirmDelete(row)}
                aria-label="Delete state license"
              />
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [confirmDelete, openEdit]
  )

  return (
    <>
      <Card
        title="State Licenses"
        bordered={false}
        extra={
          <Stack
            direction="row"
            space={8}
            wrap="wrap"
            justify="flex-end"
            align="center"
          >
            <Input
              prefix={<SearchOutlined aria-hidden="true" />}
              placeholder="Search state licenses"
              value={search}
              onChange={onSearchChange}
              allowClear
              aria-label="Search state licenses"
              // Fixed comfortable width on wide viewports; shrinks with the
              // header slot on narrow viewports so it can wrap above the Add
              // button instead of overflowing. fontSize: 16 prevents iOS
              // Safari from zooming the viewport on focus.
              style={{ width: 220, maxWidth: '100%', fontSize: 16 }}
            />
            <Tooltip
              title={canAdd ? '' : 'All states already have a license.'}
              trigger={['hover', 'focus']}
            >
              {/* Ant disabled buttons swallow pointer events, so the tooltip
                  wouldn't fire on them — the span wrapper receives hover. */}
              <span style={{ display: 'inline-flex' }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined aria-hidden="true" />}
                  onClick={openAdd}
                  disabled={!canAdd}
                >
                  Add
                </Button>
              </span>
            </Tooltip>
          </Stack>
        }
      >
        <>
          {/* Visually hidden live region so screen readers announce filter
              results as the user types. */}
          <div role="status" aria-live="polite" style={srOnlyStyle}>
            {search.trim()
              ? `${filteredLicenses.length} of ${stateLicenses.length} state licenses match`
              : ''}
          </div>
          <Table
            rowKey={(r) => r.id}
            loading={loading}
            dataSource={filteredLicenses}
            columns={columns}
            pagination={paginationProps}
            locale={{
              emptyText: search.trim()
                ? 'No state licenses match your search'
                : 'No state licenses',
            }}
            scroll={{ x: 'max-content' }}
          />
        </>
      </Card>

      <Modal
        title={editing ? 'Edit state license' : 'Add state license'}
        open={modalOpen}
        onOk={() => void submitModal()}
        confirmLoading={submitting}
        okButtonProps={{
          disabled: !isValid || (editing != null && !isDirty),
        }}
        cancelButtonProps={{ disabled: submitting }}
        onCancel={() => {
          if (submitting) return
          setModalOpen(false)
          reset(emptyLicense)
        }}
        maskClosable={!submitting}
        destroyOnClose
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            void submitModal()
          }}
          noValidate
        >
          <Stack direction="col" space={12}>
            <Stack>
              <Typography size={14}>State</Typography>
              <RHFSelect
                name="state"
                control={control}
                rules={{ required: 'Required' }}
                showSearch
                optionFilterProp="label"
                placeholder="Select state"
                options={stateOptionsForSelect}
                aria-label="State"
                aria-required="true"
              />
            </Stack>
            <Stack>
              <Typography size={14}>License number</Typography>
              <RHFInput
                name="licenseNumber"
                control={control}
                rules={{ required: 'Required' }}
                aria-label="License number"
                aria-required="true"
              />
            </Stack>
          </Stack>
          {/* Enables Enter-to-submit; Ant's Modal OK button sits outside this form */}
          <button type="submit" hidden aria-hidden tabIndex={-1} />
        </form>
      </Modal>
    </>
  )
}
